import { useState, useEffect, useRef } from 'react';
import { Image, Tooltip, Button, Space } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getImageDownloadUrls } from '@/services/common';
import { coverSource, privateCoverUrl } from './cover-source.mjs';
import './index.scss';

const CoverImage = ({ coverImage, width = 72, height = 48, enableSwitch = true, style = {} }) => {
  const { i18n, t } = useTranslation();
  const tr = key => t(`agentManagement.${key}`);
  const [currentLang, setCurrentLang] = useState('cn');
  const [failedImages, setFailedImages] = useState(() => new Set());
  const [retryVersion, setRetryVersion] = useState(0);
  const [retrying, setRetrying] = useState(false);
  const [privateImage, setPrivateImage] = useState(null);
  const retryButton = useRef(null);
  const coverGroup = useRef(null);
  const restoreRetryFocus = useRef(false);

  // 根据国际化环境设置默认显示语言
  useEffect(() => {
    const lang = i18n.language;
    if (lang === 'en' || lang === 'en-US') {
      setCurrentLang('en');
    } else {
      setCurrentLang('cn');
    }
  }, [i18n.language]);

  // 解析coverImage JSON字符串
  const parseImageUrls = (imageData) => {
    if (!imageData) return { cn: null, en: null };
    
    try {
      const parsed = typeof imageData === 'string' ? JSON.parse(imageData) : imageData;
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : { cn: null, en: null };
    } catch (error) {
      return { cn: null, en: null };
    }
  };

  const imageUrls = parseImageUrls(coverImage);
  const baseUrl = import.meta.env.VITE_PUBLIC_OSS_HOST || '';

  const cnPath = typeof imageUrls.cn === 'string' && imageUrls.cn.trim() ? imageUrls.cn : null;
  const enPath = typeof imageUrls.en === 'string' && imageUrls.en.trim() ? imageUrls.en : null;
  const currentPath = currentLang === 'cn' ? cnPath || enPath : enPath || cnPath;
  const source = coverSource(currentPath, baseUrl);
  const privateKey = source.kind === 'private' ? source.key : null;
  const privateReady = privateImage?.key === privateKey && privateImage?.version === retryVersion;
  const currentImageUrl = source.kind === 'public' ? source.url : privateReady ? privateImage?.url : null;
  const resolving = Boolean(privateKey && (!privateReady || (!privateImage.url && !privateImage.error)));
  const failed = failedImages.has(currentPath) || source.kind === 'invalid' || Boolean(privateKey && privateReady && privateImage.error);

  useEffect(() => {
    if (!privateKey) return undefined;
    let active = true;
    setPrivateImage({ key: privateKey, version: retryVersion, url: null, error: false });
    Promise.resolve().then(() => active ? getImageDownloadUrls([privateKey]) : null).then(response => {
      if (!active) return;
      const url = privateCoverUrl(response, privateKey, baseUrl);
      setPrivateImage({ key: privateKey, version: retryVersion, url, error: false });
    }).catch(() => {
      if (active) {
        setPrivateImage({ key: privateKey, version: retryVersion, url: null, error: true });
        setRetrying(false);
      }
    });
    return () => { active = false; };
  }, [privateKey, retryVersion, baseUrl]);

  useEffect(() => {
    if (!failed && restoreRetryFocus.current) {
      restoreRetryFocus.current = false;
      coverGroup.current?.focus({ preventScroll: true });
    }
  }, [currentImageUrl, failed]);

  // 检查是否有可切换的图片
  const canSwitch = enableSwitch && cnPath && enPath;

  // 切换语言
  const handleSwitch = (e) => {
    e.stopPropagation();
    setCurrentLang(prev => prev === 'cn' ? 'en' : 'cn');
  };

  // 如果没有任何图片，返回占位符
  if (!currentPath) {
    return (
      <div 
        className="cover-placeholder"
        style={{ width, height }}
      >
        <span className="placeholder-text">{tr('coverEmpty')}</span>
      </div>
    );
  }

  // 获取当前显示的语言标签
  const getCurrentLabel = () => {
    if (currentLang === 'cn') {
      return cnPath ? tr('coverCn') : tr('coverEn');
    } else {
      return enPath ? tr('coverEn') : tr('coverCn');
    }
  };

  return (
    <div className="cover-image-container" ref={coverGroup} tabIndex={-1} role="group" aria-busy={resolving || (retrying && source.kind !== 'invalid')}
      aria-label={tr(currentLang === 'cn' ? 'coverImageCn' : 'coverImageEn')}>
      <div className="cover-image-wrapper" style={{ position: 'relative' }}>
        {resolving && !failed && <div className="cover-placeholder" style={{ width, height }}>
          <span role="status">{tr('coverLoading')}</span>
        </div>}
        {failed && <div className="cover-placeholder" style={{ width, height }}>
          <Space direction="vertical" size={0} align="center">
            <span role="status" aria-label={tr(source.kind === 'invalid' ? 'coverSourceInvalid' : 'coverReadFailed')}>{tr('coverUnavailable')}</span>
            {source.kind !== 'invalid' && <Button ref={retryButton} type="link" size="small" loading={retrying} aria-label={tr('retryCover')}
              onClick={() => {
                restoreRetryFocus.current = (retryButton.current?.nativeElement || retryButton.current) === document.activeElement;
                setRetrying(true); setRetryVersion(value => value + 1);
              }}>{tr('retry')}</Button>}
          </Space>
        </div>}
        {currentImageUrl && <Image
          key={`${currentImageUrl}-${retryVersion}`}
          src={currentImageUrl}
          alt={tr(currentLang === 'cn' ? 'coverImageCn' : 'coverImageEn')}
          width={width}
          height={height}
          className="cover-image"
          style={style} // 传递style属性
          wrapperStyle={failed ? { display: 'none' } : undefined}
          onError={() => { setRetrying(false); setFailedImages(previous => new Set(previous).add(currentPath)); }}
          onLoad={() => {
            restoreRetryFocus.current = (retryButton.current?.nativeElement || retryButton.current) === document.activeElement;
            setRetrying(false);
            setFailedImages(previous => { const next = new Set(previous); next.delete(currentPath); return next; });
          }}
          preview={{
            mask: (
              <div style={{ color: 'white', fontSize: 12, textAlign: 'center' }}>
                <div>{getCurrentLabel()}</div>
                {canSwitch && <div style={{ fontSize: 10 }}>{tr('coverPreview')}</div>}
              </div>
            )
          }}
        />}
        
        {/* 切换按钮 */}
        {canSwitch && (
          <Tooltip title={tr('coverSwitch')}>
            <Button
              type="text"
              size="small"
              icon={<SwapOutlined />}
              aria-label={tr('coverSwitch')}
              className="switch-button"
              onClick={handleSwitch}
              style={{
                position: 'absolute',
                top: -3,
                right: -3,
                width: 24,
                height: 24,
                padding: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                border: 'none',
                borderRadius: '50%',
                color: 'white',
                fontSize: 12,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          </Tooltip>
        )}
        
        {/* 语言标识 */}
        {!failed && <div className="language-indicator">
          {getCurrentLabel()}
        </div>}
      </div>
    </div>
  );
};

export default CoverImage;
